import FormDatePicker from 'components/form/FormDatePicker';
import FormSelect from 'components/form/FormSelect';
import { AppConst } from 'constants/appConst';
import { format, parse } from 'date-fns';

import { useAppState } from 'hooks/useAppState';
import { useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm, useWatch } from 'react-hook-form';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { submit } from 'utils/app';
/**
 * コンテンツ公開設定モーダル
 */
export default function WsModalContentPublish(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  publishStartAt: number;
  publishEndAt: number;
  noIndexFlag: boolean;
  /** true の場合、noIndexFlag チェックボックスを表示しない（voice_chat は DB / reqSchema に項目が無いため） */
  hideNoIndexField?: boolean;
  onSubmit: (attr: { publishStartAt: number; publishEndAt: number; noIndexFlag: boolean }) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    publishStartDate: Date;
    publishStartTime: string;
    publishEndDate: Date;
    publishEndTime: string;
    noIndexFlag: boolean;
  };
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [processing, setProcessing] = useState(false);
  const { initializeAcctState } = useAppState();

  const [acctState] = useAtom(AcctState);

  const {
    control,
    register,
    handleSubmit,
    setValue,

    formState: { errors },
  } = useForm<FormStateType>();

  const timeOptions = [
    { value: '00:00', label: '0:00' },
    { value: '01:00', label: '1:00' },
    { value: '02:00', label: '2:00' },
    { value: '03:00', label: '3:00' },
    { value: '04:00', label: '4:00' },
    { value: '05:00', label: '5:00' },
    { value: '06:00', label: '6:00' },
    { value: '07:00', label: '7:00' },
    { value: '08:00', label: '8:00' },
    { value: '09:00', label: '9:00' },
    { value: '10:00', label: '10:00' },
    { value: '11:00', label: '11:00' },
    { value: '12:00', label: '12:00' },
    { value: '13:00', label: '13:00' },
    { value: '14:00', label: '14:00' },
    { value: '15:00', label: '15:00' },
    { value: '16:00', label: '16:00' },
    { value: '17:00', label: '17:00' },
    { value: '18:00', label: '18:00' },
    { value: '19:00', label: '19:00' },
    { value: '20:00', label: '20:00' },
    { value: '21:00', label: '21:00' },
    { value: '22:00', label: '22:00' },
    { value: '23:00', label: '23:00' },
  ];

  const navigate = useNavigate();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  const publishStartDate = useWatch({
    control: control,
    name: 'publishStartDate',
  });

  const publishEndDate = useWatch({
    control: control,
    name: 'publishEndDate',
  });

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onClose = () => {
    props.setOpen(false);
  };

  const onAfterOpen = () => {
    // 公開開始日時の設定
    if (props.publishStartAt) {
      const publishStartAt = new Date(props.publishStartAt);
      setValue('publishStartDate', publishStartAt);
      setValue('publishStartTime', format(publishStartAt, 'HH:mm'));
    } else {
      setValue('publishStartDate', null);
      setValue('publishStartTime', '00:00');
    }

    // 公開終了日時の設定
    if (props.publishEndAt) {
      const publishEndAt = new Date(props.publishEndAt);
      setValue('publishEndDate', publishEndAt);
      setValue('publishEndTime', format(publishEndAt, 'HH:mm'));
    } else {
      setValue('publishEndDate', null);
      setValue('publishEndTime', '00:00');
    }
    // インデックス除外フラグの設定
    setValue('noIndexFlag', props.noIndexFlag);
  };

  /**
   * 設定ボタン押下時の処理
   */
  const onSubmit = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit((data: FormStateType) => {
        try {
          // 公開開始日時の取得
          let publishStartAt = null;
          if (data.publishStartDate) {
            const ymd = format(data.publishStartDate, 'yyyy-MM-dd');
            publishStartAt = parse(
              `${ymd} ${data.publishStartTime}`,
              'yyyy-MM-dd HH:mm',
              new Date()
            ).getTime();
          }

          // 公開終了日時の取得
          let publishEndAt = null;
          if (data.publishEndDate) {
            const ymd = format(data.publishEndDate, 'yyyy-MM-dd');
            publishEndAt = parse(
              `${ymd} ${data.publishEndTime}`,
              'yyyy-MM-dd HH:mm',
              new Date()
            ).getTime();
          }

          // 呼び出し元画面に結果を送信
          props.onSubmit({
            publishStartAt: publishStartAt,
            publishEndAt: publishEndAt,
            noIndexFlag: data.noIndexFlag || false,
          });

          // モーダルを閉じる
          onClose();
        } catch (e) {
          if (e.response.statusCode === AppConst.httpStatusCode.entryUnprocessable) {
            // トライアルプラン（利用期間切れ）の場合はTOPに遷移
            initializeAcctState(acctState.company.cd);
            navigate('/');
          }
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        // onRequestClose={onClose}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '700px',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        <div
          id="settings-modal"
          className="modal2 modal-content rounded-4 "
          style={{ maxWidth: 'none' }}
        >
          <div className="modal-header border-bottom-0">
            <button onClick={onClose} type="button" className="btn-close btn-circle-close">
              <img src="/img/icon/multiply.svg" alt="x" />
            </button>
          </div>
          <div className="modal-body">
            <h3 className="modal-title text-center mt-5" id="settings-modal-label">
              あと少しです。
              <br />
              公開設定をしましょう
            </h3>

            <div className="row justify-content-center align-items-center pt-1">
              <p className="col-3 col-md-3 text-end">開始日</p>
              <div className="col-6 col-md-7">
                <div className="d-flex align-items-start">
                  <FormDatePicker
                    name="publishStartDate"
                    placeholder="即時公開"
                    className="form-controll-small"
                    control={control}
                    errors={errors}
                  />
                  {publishStartDate && (
                    <FormSelect
                      name={`publishStartTime`}
                      register={register}
                      errors={errors}
                      className="ms-2 form-controll-small pe-4"
                      options={timeOptions}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="row justify-content-center align-items-center pt-1">
              <p className="col-3 col-md-3  text-end">終了日</p>
              <div className="col-6 col-md-7">
                <div className="d-flex align-items-start">
                  <FormDatePicker
                    name="publishEndDate"
                    placeholder="無期限"
                    className="form-controll-small"
                    control={control}
                    errors={errors}
                  />
                  {publishEndDate && (
                    <FormSelect
                      name={`publishEndTime`}
                      register={register}
                      errors={errors}
                      className="form-controll-small ms-2 pe-4"
                      options={timeOptions}
                    />
                  )}
                </div>
              </div>
            </div>

            {!props.hideNoIndexField && (
              <div className="row justify-content-center align-items-center pt-1 mt-0">
                <p className="col-2 col-md-2 mb-0 text-end"></p>
                <div className="col-6 col-md-7 d-flex align-items-start">
                  <input
                    id="noIndexFlag"
                    name="noIndexFlag"
                    type="checkbox"
                    className="form-check-input" // Bootstrapのチェックボックス用クラス
                    {...register('noIndexFlag')}
                  />
                  <label htmlFor="noIndexFlag" className="form-label ms-2">
                    検索エンジンがインデックスしないようにする
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer border-top-0 justify-content-center mt-4">
            <button onClick={onClose} type="button" className="btn btn-secondary">
              キャンセル
            </button>
            <button onClick={onSubmit} type="button" className="btn btn-submit">
              設定完了
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
}
